import type { LatLng } from "./types";

export const WEB_MERCATOR_MAX_LAT = 85.05112878;
export const EARTH_RADIUS_METERS = 6_378_137;

export interface TileFraction {
  x: number;
  y: number;
}
export interface RegionBounds {
  south: number;
  west: number;
  north: number;
  east: number;
  crossesAntimeridian: boolean;
}

export interface TileRange {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  z: number;
}

export function clampLatitude(lat: number): number {
  return Math.max(-WEB_MERCATOR_MAX_LAT, Math.min(WEB_MERCATOR_MAX_LAT, lat));
}

export function normalizeTileX(x: number, z: number): number {
  const scale = 2 ** z;
  return ((x % scale) + scale) % scale;
}

export function latLngToTileFraction(point: LatLng, z: number): TileFraction {
  const scale = 2 ** z;
  const lat = (clampLatitude(point.lat) * Math.PI) / 180;
  return {
    x: ((point.lng + 180) / 360) * scale,
    y: ((1 - Math.asinh(Math.tan(lat)) / Math.PI) / 2) * scale,
  };
}

export function tileToLatLng(x: number, y: number, z: number): LatLng {
  const scale = 2 ** z;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  return {
    lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
    lng: (x / scale) * 360 - 180,
  };
}

export function regionBounds(center: LatLng, radiusMeters: number): RegionBounds {
  const angular = radiusMeters / EARTH_RADIUS_METERS;
  const lat = (center.lat * Math.PI) / 180;
  const lng = (center.lng * Math.PI) / 180;
  const south = Math.max(-Math.PI / 2, lat - angular);
  const north = Math.min(Math.PI / 2, lat + angular);
  const longitudeDelta = Math.asin(Math.min(1, Math.sin(angular) / Math.max(1e-12, Math.cos(lat))));
  const rawWest = lng - longitudeDelta;
  const rawEast = lng + longitudeDelta;
  const west = ((((rawWest * 180) / Math.PI + 180) % 360) + 360) % 360 - 180;
  const east = ((((rawEast * 180) / Math.PI + 180) % 360) + 360) % 360 - 180;
  return {
    south: (south * 180) / Math.PI,
    west,
    north: (north * 180) / Math.PI,
    east,
    crossesAntimeridian: west > east,
  };
}

/** Returns unwrapped x ranges; consumers wrap x only when forming the XYZ URL. */
export function tileRangesForRegion(center: LatLng, radiusMeters: number, z: number): TileRange[] {
  const bounds = regionBounds(center, radiusMeters);
  const scale = 2 ** z;
  const top = Math.max(0, Math.floor(latLngToTileFraction({ lat: bounds.north, lng: 0 }, z).y));
  const bottom = Math.min(scale - 1, Math.floor(latLngToTileFraction({ lat: bounds.south, lng: 0 }, z).y));
  const centerX = latLngToTileFraction(center, z).x;

  let westX = latLngToTileFraction({ lat: center.lat, lng: bounds.west }, z).x;
  let eastX = latLngToTileFraction({ lat: center.lat, lng: bounds.east }, z).x;
  while (westX - centerX > scale / 2) westX -= scale;
  while (westX - centerX < -scale / 2) westX += scale;
  while (eastX - centerX > scale / 2) eastX -= scale;
  while (eastX - centerX < -scale / 2) eastX += scale;
  if (eastX < westX) eastX += scale;

  return [{ minX: Math.floor(westX), maxX: Math.floor(eastX), minY: top, maxY: bottom, z }];
}

export function metersPerPixelAtLatitude(lat: number, z: number, tileSize = 256): number {
  return (
    (Math.cos((clampLatitude(lat) * Math.PI) / 180) * 2 * Math.PI * EARTH_RADIUS_METERS) /
    (tileSize * 2 ** z)
  );
}

export interface ImageryZoomChoice {
  zoom: number;
  columns: number;
  rows: number;
  tileCount: number;
  textureWidth: number;
  textureHeight: number;
  metersPerPixel: number;
  meetsTarget: boolean;
}

export function chooseImageryZoom(options: {
  center: LatLng;
  radiusMeters: number;
  minZoom: number;
  maxZoom: number;
  tileSize: number;
  targetMetersPerPixel: number;
  maxTextureSize: number;
  maxTiles: number;
}): ImageryZoomChoice {
  for (let zoom = options.maxZoom; zoom >= options.minZoom; zoom -= 1) {
    const [range] = tileRangesForRegion(options.center, options.radiusMeters, zoom);
    if (!range) continue;
    const columns = range.maxX - range.minX + 1;
    const rows = range.maxY - range.minY + 1;
    const tileCount = columns * rows;
    const textureWidth = columns * options.tileSize;
    const textureHeight = rows * options.tileSize;
    if (
      tileCount <= options.maxTiles &&
      textureWidth <= options.maxTextureSize &&
      textureHeight <= options.maxTextureSize
    ) {
      const metersPerPixel = metersPerPixelAtLatitude(options.center.lat, zoom, options.tileSize);
      return {
        zoom,
        columns,
        rows,
        tileCount,
        textureWidth,
        textureHeight,
        metersPerPixel,
        meetsTarget: metersPerPixel <= options.targetMetersPerPixel,
      };
    }
  }
  throw new Error("No imagery zoom fits the configured tile and texture budgets.");
}
